import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})

/**
 * If we get a 503 http response, the server is unavailable. We will use this interceptor to catch that and show the user a message letting them know.
 */
export class ServerUnavailableInterceptor implements HttpInterceptor {
  private isAlertAlreadyShown = false; // to stop us from showing duplicate alerts

  constructor(
    private alertCtrl: AlertController,
    private router: Router,
  ) {}

  /**
   * The purpose of this Intercept function will identify the response from the server, when the server is unavailable it will show the message to the user.
   *
   * @param request - Represents the request on its way to the server.
   * @param next - Represents next step in the interceptor chain.
   * @returns - Observable that wraps up an HttpEvent object.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === 0) {
          // A client-side or network error occurred. Handle it accordingly.
          console.error('An error occurred:', errorResponse.error);
        } else if (errorResponse.status === 503) {
          console.error('[Server Unavailable] 503', errorResponse, request);

          // The backend returned an unsuccessful response code.
          // Alert is not expected to be displayed except for app launch where the UserGuard redirection is getting preference over maintenance interceptor.
          if (this.router.url !== '/maintenance-downtime' && !this.isAlertAlreadyShown) {
            this.isAlertAlreadyShown = true;

            this.showAlert();
          }
          // Returning null to exclude component level error existance checks.
          // This is to avoid displaying login error when the call fails because of maintenance.
          // Its a workaround for the hardcoded login error message.
          return EMPTY;
        }
        // Return an observable with a user-facing error message.
        return throwError(errorResponse);
      }),
    );
  }

  /**
   * ShowAlert will show the message to the user and also avoided the duplicate alerts.
   */
  async showAlert() {
    const alert = await this.alertCtrl.create({
      header: 'Maintenance',
      message: 'Mable is currently undergoing maintenance. Please try again later',
      buttons: [{ text: 'Ok', handler: () => {} }],
    });

    alert.onDidDismiss().then(() => {
      this.isAlertAlreadyShown = false;
      // Redirect the user to maintenance page to stop them from using the app.
      this.router.navigate(['/maintenance-downtime']);
    });

    await alert.present();
  }
}
